import React, {useEffect, useState} from 'react'
import {BoxProps, Button, Flex, Text} from '../../vanilla'
import {MinusIcon, PlusIcon} from '../icons'
import {quantityInputButton, quantityInputRecipeButton, quantityInputRecipeInput} from './style.css'
import {ProductItem} from '../../store/BasketStore'
import {useRemoveFromCartTracking} from '../../analytics/click-event-tracking'
import {useAddToCartTracking} from '../../analytics/click-event-tracking'
import {useSlotExpiration} from '../../store/hooks/useSlotExpiration'
import {Nullable} from '../../types/utils'
import {RestrictionModal} from '../update-cart-quantity'
import {useLocation} from 'react-router-dom'
import {redirectIfNotRegistered} from "../../utils/login-redirect-utils"
import {
  useBasketStore,
  useCustomerStore,
  useOrderStore
} from '../../store/hooks/useStore'
import useNavigation from '../../hooks/use-navigation'
import classNames from 'classnames'

export interface QuantityInputProps extends Omit<BoxProps<'div'>, 'onChange' | 'value'> {
  product?: ProductItem
  quantity: number
  setQuantity: React.Dispatch<React.SetStateAction<number>>
  size?: 'md' | 'sm'
  maxQuantity?: number
  variant?: 'primary' | 'outlineDark'
  disabled?: boolean
  cartLocation?: string
  viewingCategoryId?: Nullable<string>
  searchQuery?: Nullable<string>
  hitItemIndex?: number
  restrictionModal?: RestrictionModal
  inDrawer?: boolean
  isSponsored?: boolean
}

const QuantityInput = ({
  product,
  quantity,
  setQuantity,
  size = 'md',
  maxQuantity,
  disabled,
  variant = 'primary',
  cartLocation,
  viewingCategoryId,
  searchQuery,
  hitItemIndex,
  restrictionModal,
  inDrawer = false,
  isSponsored = false,
  ...rest
}: QuantityInputProps) => {
  const [isMaxLimitReached, setIsMaxLimitReached] = useState(false)
  const [incrementClickedAtMaxQuantity, setIncrementClickedAtMaxQuantity] = useState(false)
  const [clickedQuantity, setClickedQuantity] = useState(quantity)
  const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()
  const basketStore = useBasketStore()
  const customerStore = useCustomerStore()
  const {editMode} = useOrderStore()
  const sendRemoveFromCartData = useRemoveFromCartTracking()

  // Re-direct if customer is not logged in
  const navigate = useNavigation()
  const {pathname} = useLocation()
  const {isRegistered, loading: loadingCustomer} = useCustomerStore()

  // Check if max quantity is reached
  useEffect(() => {
    if (maxQuantity && quantity >= maxQuantity) {
      setIsMaxLimitReached(true)
    } else {
      setIsMaxLimitReached(false)
    }
  }, [quantity, maxQuantity])

  useEffect(() => {
    setClickedQuantity(quantity)
  }, [quantity])

  const updateQuantity = (e: React.MouseEvent, isIncrease: boolean) => {
    customerStore.setLastActiveToNow()
    e.preventDefault()
    e.stopPropagation()

    if (editMode && isReservationExpired()) {
      // If in edit mode and slot has expired send to order history and don't add to basket
      handleEditReservationExpired()
      return
    }

    customerStore.setLastActiveToNow()

    // Increase the current quantity state
    const newQuantity = isIncrease ? quantity + 1 : quantity - 1

    // Stop increase from happening if the quantity is already at max quantity
    if (isIncrease && maxQuantity && newQuantity > maxQuantity) {
      setIncrementClickedAtMaxQuantity(true)
      setIsMaxLimitReached(true)
      return
    }

    if (!isIncrease && incrementClickedAtMaxQuantity) {
      setIncrementClickedAtMaxQuantity(false)
      setIsMaxLimitReached(false)
    }

    const productIndex = basketStore.getUpdateProductIndex(product?.id)

    if (productIndex >= 0) {
      basketStore.updateProductToUpdateProductQuantity(product?.id, newQuantity)
    } else {
      // If in edit mode and slot has expired send to order history and don't add to basket
      handleEditReservationExpired()
      basketStore.addProductToUpdate({product: {...product, isSponsored}, quantity: newQuantity})
    }

    setQuantity(newQuantity)
  }

  const buttons = quantityInputRecipeButton({
    size,
    variant,
  })
  const input = quantityInputRecipeInput({size, variant})
  const sendAddToCartData = useAddToCartTracking(basketStore.basket)

  const addToCartData = {
    product,
    cartLocation,
    viewingCategoryId,
    searchQuery,
    hitItemIndex
  }

  return (
    <Flex alignItems="center" flexDirection="column" {...rest}>
      <Flex>
        <Button
          data-test-selector="decrease-quantity-button"
          extraClassName={classNames(quantityInputButton, buttons)}
          aria-label="Decrease quantity"
          flexShrink="0"
          variant={variant}
          onClick={(e) => {
            if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer})) {
              updateQuantity(e, false)
              const removeFromCartData = {
                product,
                cartLocation,
                viewingCategoryId,
                searchQuery,
                hitItemIndex
              }
              sendRemoveFromCartData(removeFromCartData)
            }
          }}
          disabled={disabled}
          size="sm"
          iconLeft={
            <MinusIcon
              style={{
                height: size === 'sm' ? 10 : 12,
                width: size === 'sm' ? 10 : 12,
                pointerEvents: 'none',
              }}
            />
          }
        />
        <Text
          variant="unstyled"
          className={input}
          flexShrink="1"
          alignSelf="stretch"
          data-test-selector={inDrawer ? 'cart-quantity-value' : 'plp-quantity-value'}
          style={variant === 'outlineDark' ? {} : {color: '#0097cc'}}
        >
          {`${clickedQuantity}`}
        </Text>

        <Button
          data-test-selector="increase-quantity-button"
          variant={variant}
          extraClassName={classNames(quantityInputButton, buttons)}
          aria-label="Increase quantity"
          flexShrink="0"
          onClick={(e) => {
            if (isMaxLimitReached) {
              return
            }
            if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer})) {
              updateQuantity(e, true)
              sendAddToCartData(addToCartData)
            }
          }}
          disabled={disabled}
          style={{
            opacity: isMaxLimitReached ? 0.3 : disabled ? 0.65 : 1,
            cursor: isMaxLimitReached ? 'not-allowed' : 'pointer',
          }}
          size="sm"
          iconLeft={
            <PlusIcon
              style={{
                height: size === 'sm' ? 10 : 12,
                width: size === 'sm' ? 10 : 12,
                pointerEvents: 'none',
              }}
            />
          }
        />
      </Flex>
      {cartLocation !== 'Quick Search'
      && (isMaxLimitReached)
      && ( <Text color="accent0"> Maximum reached</Text> )}
    </Flex>
  )
}

export default QuantityInput
